import { createGlobalStyle } from 'styled-components'
import { themeGet } from 'styled-system'

/**
 * Page-wide print rules for the Individual Resource pages.
 *
 * These intentionally target elements across the whole page (not just this
 * component) via stable hooks: the `data-print-hidden` attributes the pages
 * add to their controls, the `.individual-resource-print-page` container
 * class, and a few global widget ids/classes. We avoid matching
 * styled-components class names directly, since those readable names are
 * stripped in production builds and would silently stop matching.
 */
export const PrintStyles = createGlobalStyle`
  @media print {
    /* Hide non-print controls on this page */
    [data-print-hidden='navigation'],
    [data-print-hidden='mark-done'],
    [data-print-hidden='teach-button'],
    [data-print-hidden='language-toggle'],
    [data-print-hidden='print-button'],
    [data-print-hidden='program-nav'],
    [data-print-hidden='references'],
    ._pendo-step-container-styles,
    [class*='_pendo'],
    #help-button,
    .global-nav-container {
      display: none !important;
    }

    /* Controls are meaningless on paper — hide buttons, toggles, and inputs */
    .individual-resource-print-page button,
    .individual-resource-print-page [role='button'],
    .individual-resource-print-page input,
    .individual-resource-print-page select,
    .individual-resource-print-page textarea {
      display: none !important;
    }

    /* Keep link text on paper, but strip the clickable affordance so it
       reads as plain text */
    .individual-resource-print-page a[href] {
      pointer-events: none;
      cursor: default;
      text-decoration: none;
      color: inherit !important;
    }

    /* Reserve a dedicated band at the bottom of every printed page so the
       copyright never overlaps the flowing content above it */
    @page {
      margin-bottom: 1.25in;
    }

    /* Keep flowing content clear of the fixed footer band */
    .individual-resource-print-page {
      padding-bottom: 3rem;
    }

    /* Show the copyright line at the bottom of each printed page. The opaque
       background masks any content that paginates into the footer band. */
    [data-print-copyright] {
      display: block !important;
      position: fixed;
      bottom: 0;
      left: 0;
      right: 0;
      padding: 0.75rem 0.5rem 0.5rem;
      background: ${themeGet('colors.white')};
    }

    /* Scale print output down to 90% in print preview/output */
    @supports (zoom: 1) {
      .individual-resource-print-page {
        zoom: 0.9;
      }
    }
    @supports not (zoom: 1) {
      .individual-resource-print-page {
        transform: scale(0.9);
        transform-origin: top left;
        width: 111.112%;
      }
    }
  }
`

/**
 * High-school-only print fixes. The HS layout differs from K-8/MS: it uses
 * fixed 55rem content widths whose responsive shrink rules are `@media screen`
 * (so they don't apply in print and overflow the page), and a header that
 * pairs the title with a fixed-width decorative image in a flex row.
 */
export const HighSchoolPrintStyles = createGlobalStyle`
  @media print {
    /* Cap content to the printable width so headings/cards stop bleeding off
       the left and right page edges */
    .individual-resource-print-page .content,
    .individual-resource-print-page .content * {
      max-width: 100% !important;
    }

    /* Stack header content and drop the decorative image so the title gets the
       full width instead of collapsing to one letter per line */
    .individual-resource-print-page .content {
      display: block !important;
    }

    .individual-resource-print-page .resource-image {
      display: none !important;
    }
  }
`
